import { IconButton, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { Icon } from "../../../components/Icon/Icon";
import { colors } from "../../../styles/colors";

/**
 * Human label for a strongbox `type`. Known types get an i18n label,
 * unknown ones fall back to the raw value so nothing is hidden.
 */
export const getDocumentTypeLabel = (
  t: TFunction,
  type?: string | null
): string => {
  if (!type) return t("ged.typeOther", "Autre");
  const known: Record<string, string> = {
    assurance: t("ged.typeInsurance", "Assurance"),
    insurance_certificate: t(
      "ged.typeInsuranceCertificate",
      "Certificat d'assurance"
    ),
    identity: t("ged.typeIdentity", "Pièce d'identité"),
    bill: t("ged.typeBill", "Facture"),
    license_card: t("ged.typeLicense", "Permis de conduire"),
    registration_certificate: t("ged.typeRegistration", "Carte grise"),
    contract: t("ged.typeContract", "Contrat"),
    technical_control: t("ged.typeTechnicalControl", "Contrôle technique"),
  };
  return known[type] ?? type;
};

/** Human label for the computed `source` of a document. */
export const getDocumentSourceLabel = (
  t: TFunction,
  source?: TFleetDocumentSource | null
): string => {
  switch (source) {
    case "vehicle":
      return t("ged.sourceVehicle", "Véhicule");
    case "collaborator":
      return t("ged.sourceCollaborator", "Collaborateur");
    case "sinister":
      return t("ged.sourceSinister", "Sinistre");
    case "event":
      return t("ged.sourceEvent", "Événement");
    case "contract":
      return t("ged.sourceContract", "Contrat");
    default:
      return "—";
  }
};

const sourceColors: Record<string, string> = {
  vehicle: colors.primary,
  collaborator: colors.successColor,
  sinister: colors.errorColor,
  event: colors.warningColor,
  contract: colors.electricBlue,
};

const pillStyle = (color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  color,
  backgroundColor: color + "20",
});

type ColumnHandlers = {
  onOpenDocument: (doc: TFleetDocument) => void;
};

export const buildColumnsDocumentsList = (
  t: TFunction,
  { onOpenDocument }: ColumnHandlers
) => [
  {
    header: t("ged.columnName", "Nom"),
    field: "name",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="file" width={18} height={18} color={colors.mutedText} />
          <span>
            {doc.name ||
              doc.document_type_name ||
              getDocumentTypeLabel(t, doc.type)}
          </span>
        </div>
      );
    },
  },
  {
    header: t("ged.columnType", "Type"),
    field: "type",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      const label = doc.document_type_name || getDocumentTypeLabel(t, doc.type);
      return <span style={pillStyle(colors.primary)}>{label}</span>;
    },
  },
  {
    header: t("ged.columnVehicle", "Véhicule"),
    field: "vehicle_number",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      return <span>{doc.vehicle_number || "—"}</span>;
    },
  },
  {
    header: t("ged.columnCollaborator", "Collaborateur"),
    field: "user_lastname",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      const fullName = [doc.user_firstname, doc.user_lastname]
        .filter(Boolean)
        .join(" ");
      return <span>{fullName || "—"}</span>;
    },
  },
  {
    header: t("ged.columnSource", "Source"),
    field: "source",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      const color = sourceColors[doc.source] ?? colors.mutedText;
      return (
        <span style={pillStyle(color)}>
          {getDocumentSourceLabel(t, doc.source)}
        </span>
      );
    },
  },
  {
    header: t("ged.columnDate", "Date"),
    field: "created_at",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      return (
        <span>
          {doc.created_at ? dayjs(doc.created_at).format("DD-MM-YYYY") : "—"}
        </span>
      );
    },
  },
  {
    header: "",
    field: "",
    render: (rowData: Record<string, any>) => {
      const doc = rowData as TFleetDocument;
      if (!doc.scan_id) return null;
      return (
        <Tooltip title={t("ged.openDocument", "Ouvrir le document")}>
          <IconButton
            aria-label="Open document" onClick={() => onOpenDocument(doc)}>
            <Icon
              name="external-link"
              width={"20px"}
              height={"20px"}
              color={colors.BLACK}
            />
          </IconButton>
        </Tooltip>
      );
    },
  },
];
